import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { SettingsPanel } from '../settings/SettingsPanel';
import type { Density, Theme } from '../theme';
/**
 * The white wordmark, not the dark one: the sidebar is navy in BOTH themes by design
 * (DESIGN.md keeps the chrome dark regardless), so a single light-on-dark asset is correct
 * everywhere and there is deliberately no theme-swapped pair here.
 */
import logoUrl from '../assets/cydome-logo-light.png';
import { Avatar } from './Avatar';
import { Icon, type IconName } from './Icon';

export interface NavItem {
  label: string;
  /**
   * Content rendered when this item is selected. Omit for surfaces that arrive
   * in a later epic — the shell shows a placeholder so the nav still responds.
   */
  content?: React.ReactNode;
  /**
   * Leading Feather-style icon (DESIGN.md "Shapes"). Carried on the ITEM rather than
   * resolved from a label→icon map inside this component on purpose: the three shells
   * ship three different nav vocabularies ("Sites" vs "Companies" vs "My Certificates"),
   * so a central map here would have to know all of them, would silently fall through to
   * nothing when a shell renamed a slot, and would make the label — a display string —
   * load-bearing for presentation. Optional, so a shell may add slots without one.
   */
  icon?: IconName;
  /**
   * Optional inline count badge (DESIGN.md nav-item: "count badges inline"; the prototype
   * shows `Sites 13`, `Vendor Directory 10`, `Device Directory 23`).
   *
   * **Absent means "not known", and that is a real state** — leave it `undefined` rather
   * than defaulting to 0, because a badge reading `0` next to a section that in fact holds
   * 13 sites is worse than no badge at all: it is a confident wrong answer on a compliance
   * tool. Shells must only pass a count they have actually loaded, and must never issue a
   * request purely to fill one in.
   */
  count?: number;
}

export interface ShellChromeProps {
  /** Role-appropriate label under the brand, e.g. "Compliance Manager". */
  roleLabel: string;
  navItems: NavItem[];
  theme: Theme;
  density: Density;
  onToggleTheme: () => void;
  onToggleDensity: () => void;
  /**
   * When present, rendered in the content pane INSTEAD of the active nav item's
   * content (the sidebar/topbar are unchanged). The in-pane full-page override for
   * the Framework detail view (Story 4.3) — no router library (UX: index-based nav +
   * in-pane full-page + Back).
   */
  contentOverride?: React.ReactNode;
  /**
   * Fired when a sidebar nav item is selected (alongside the internal active-index
   * change). Lets the owner clear any `contentOverride` so selecting a nav item
   * never leaves a stale full-page override behind a lying topbar title (Story 4.3).
   */
  onNavSelect?: (index: number) => void;
  /**
   * Optional CONTROLLED active index (Story 4.4). When provided, the shell renders
   * this index (the cockpit drill controller derives it from its back stack) instead
   * of its own internal selection — so drilling across nav sections keeps the
   * sidebar highlight / topbar title in sync with the pane. When absent, the shell
   * falls back to tracking the selection internally (uncontrolled, as before).
   */
  activeIndex?: number;
  /**
   * Optional topbar notification bell (Story 4.5). Rendered chrome-only in the
   * right-side cluster (before Settings); the owner (`CockpitShell`) builds it wired
   * to the drill controller, keeping routing out of this chrome-only layout.
   */
  notificationBell?: React.ReactNode;
}

/** Neutral placeholder for nav targets whose real surface lands in a later epic. */
function SectionPlaceholder({ label }: { label: string }): React.JSX.Element {
  return (
    <>
      <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>{label}</h1>
      <p className="muted" style={{ margin: 0 }}>
        This surface arrives in a later epic.
      </p>
    </>
  );
}

/**
 * Shared Deep Navy chrome (navy sidebar + topbar + content pane). Each role's
 * shell supplies its own nav items — the chrome never changes, only the
 * role-scoped navigation does (UX-DR9). Selecting a nav item swaps the content
 * pane and topbar title; the navy sidebar stays dark in both themes.
 */
export function ShellLayout({
  roleLabel,
  navItems,
  theme,
  density,
  onToggleTheme,
  onToggleDensity,
  contentOverride,
  onNavSelect,
  activeIndex: controlledIndex,
  notificationBell,
}: ShellChromeProps): React.JSX.Element {
  const { user, signOut } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [internalIndex, setInternalIndex] = useState(0);
  // Controlled when the owner supplies `activeIndex` (the cockpit drill controller),
  // else uncontrolled (internal state) — same family as the React controlled-input pattern.
  const activeIndex = controlledIndex ?? internalIndex;

  const active = navItems[activeIndex] ?? navItems[0];

  return (
    <div className="app-shell">
      <aside className="sidebar on-navy" data-surface="navy" aria-label="Primary">
        {/* Company mark above the product name — they are two different things, so the logo
            does not replace the wordmark. `alt="Cydome"` names the company; "Vendor
            Compliance" stays real text so the product is still announced, still searchable,
            and still legible if the image fails to load. */}
        <div className="sidebar__brand">
          <img className="sidebar__logo" src={logoUrl} alt="Cydome" />
          <span className="sidebar__product">Vendor Compliance</span>
        </div>
        <div className="sidebar__section">{roleLabel}</div>
        <nav>
          {navItems.map((item, i) => (
            <button
              key={item.label}
              type="button"
              className={`nav-item${i === activeIndex ? ' is-active' : ''}`}
              {...(i === activeIndex ? { 'aria-current': 'page' as const } : {})}
              onClick={() => {
                setInternalIndex(i);
                onNavSelect?.(i);
              }}
            >
              {item.icon && <Icon name={item.icon} size={17} className="nav-item__icon" />}
              <span className="nav-item__label">{item.label}</span>
              {/* The badge is `aria-hidden` and the label is a plain span, which together keep
                  this button's accessible name EXACTLY the label. That is load-bearing: the
                  unit tests and the UAT sweep both reach every section via
                  `getByRole('button', { name: 'Sites' })`, an exact match that "Sites 13"
                  would not satisfy — so exposing the count here would silently break
                  navigation for every one of those tests. It is also the better reading
                  order: a screen-reader user tabbing the sidebar wants nine destinations,
                  not nine destinations interleaved with inventory numbers, and the
                  destination itself states its own count on arrival. */}
              {item.count !== undefined && (
                <span className="nav-item__count" aria-hidden="true" data-numeric>
                  {item.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </aside>

      <div className="main">
        <header className="topbar">
          <div className="topbar__title">{active?.label}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {user && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {/* The topbar identity tile the prototype carries (DESIGN.md avatarPalette
                    covers "user" avatars). The email stays: the prototype showed the tile
                    alone, but dropping the address would remove the only place a user can
                    confirm WHICH account they are acting as — which matters more here than
                    matching the mock, since this app is multi-tenant and one person may hold
                    accounts in several companies. Tile is aria-hidden; the email names it. */}
                <Avatar id={user.id} name={user.email} />
                <span className="muted" data-numeric style={{ fontSize: 13 }}>
                  {user.email}
                </span>
              </span>
            )}
            {/* DESIGN.md's first-named 38px icon button, and the shape both prototype shots
                show (`design/screenshots/dash.png` = moon on light, `dark.png` = sun on dark):
                the glyph is the DESTINATION, not the current state.

                This was blocked for a long time for a concrete reason — there was no icon
                system, and the only alternative was the emoji DESIGN.md bans. While it was
                textual, its accessible name had to LEAD with the visible words ("Dark mode —
                switch theme") to satisfy WCAG 2.5.3 Label in Name. With no visible text left,
                2.5.3 no longer applies (it governs the relationship between a visible label
                and the name), so the name is now free to be the plain instruction.

                CHANGING THIS STRING BREAKS TWO THINGS AT ONCE: `App.test.tsx` finds the
                toggle by it, and `uat/specs/07-design-alignment.spec.ts`'s `setTheme()` helper
                — the only way the 60-screenshot sweep switches theme — matches the same
                regex. Both are kept in lockstep with the pattern below. */}
            <button
              type="button"
              className="icon-button"
              data-icon-only="true"
              onClick={onToggleTheme}
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
            >
              <Icon name={theme === 'light' ? 'moon' : 'sun'} size={18} />
            </button>
            {notificationBell}
            <button
              type="button"
              className="icon-button"
              data-icon-only="true"
              onClick={() => setSettingsOpen(true)}
              aria-label="Open settings"
            >
              <Icon name="settings" size={18} />
            </button>
            {/* Sign out keeps its visible word: it is destructive-ish and infrequent, and the
                prototype draws it as icon + text too. The icon is `aria-hidden`, so the
                accessible name stays exactly "Sign out" — three test files query it by that. */}
            <button type="button" className="icon-button" onClick={() => void signOut()}>
              <Icon name="log-out" size={15} />
              Sign out
            </button>
          </div>
        </header>

        <main className="content">
          <div className="content__inner stack">
            {contentOverride ?? active?.content ?? <SectionPlaceholder label={active?.label ?? ''} />}
          </div>
        </main>
      </div>

      {settingsOpen && (
        <SettingsPanel
          theme={theme}
          density={density}
          onToggleTheme={onToggleTheme}
          onToggleDensity={onToggleDensity}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </div>
  );
}
